import { useState } from 'react';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Card } from '@/ui/Card';
import { ConfirmDialog, Dialog } from '@/ui/Dialog';
import { Field, Input, Select, Textarea } from '@/ui/Field';
import { SkeletonFilas } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import { EstadoVacio } from '@/components/EstadoVacio';
import {
  AUDIENCIAS_DE_REGLA, CANALES_DE_REGLA, MODOS_DE_REGLA, PARAMETRO_DE_AUDIENCIA,
  useAutomatizaciones, useBorrarRegla, useDispararEvento, useGuardarRegla, useRegla,
  type OpcionesDeAudiencia, type Regla, type TipoDeEvento,
} from './api';

/**
 * Automatizaciones — evento del dominio → regla → comunicación.
 *
 * ## Lo que cambia
 *
 * - **El estado de la bitácora se lee.** Era la palabra cruda del servidor
 *   (`procesado`, `omitido`, `error`) pintada con un color de un mapa: el mismo
 *   dato en cuatro tonos y sin nada más que los distinga.
 * - **La fecha de la bitácora está en la hora de quien mira**: era
 *   `e.created_at.replace('T',' ').slice(0,16)`, o sea UTC en crudo.
 * - **El parámetro de la audiencia se declara una vez.** Estaba repartido en
 *   seis `if` dentro de tres funciones —pintar, leer y guardar—, y las tres
 *   tenían que decir lo mismo a mano; `PARAMETRO_DE_AUDIENCIA` es ahora la única
 *   fuente.
 * - **Al cambiar la audiencia ya no se pierde el valor elegido.**
 *   `onRuleAudChange()` repintaba el `<select>` con `_autoAudParam(t, '')`: el
 *   segundo argumento, vacío a la fuerza, borraba la selección incluso al volver
 *   al mismo tipo.
 * - **Probar un evento dice a quién afectó.** Pedía «ID del colaborador» a pelo,
 *   sin comprobar que existiera.
 */
export function Automatizaciones() {
  const a = useAutomatizaciones(true);
  const borrar = useBorrarRegla();
  const [editando, setEditando] = useState<number | null | undefined>(undefined);
  const [borrando, setBorrando] = useState<Regla | null>(null);
  const [probando, setProbando] = useState(false);

  const reglas = a.reglas.data ?? [];

  return (
    <>
      <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-4 font-bold text-brand-ink">Automatizaciones</h2>
          <p className="mt-1 text-3 text-muted">
            Un evento del dominio dispara una regla configurable que genera una comunicación.
            Configuración antes que código.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" size="sm" onClick={() => setProbando(true)}>
            <i className="bi bi-lightning" aria-hidden="true" /> Probar evento
          </Button>
          <Button size="sm" onClick={() => setEditando(null)}>
            <i className="bi bi-plus-circle" aria-hidden="true" /> Nueva regla
          </Button>
        </div>
      </header>

      {a.reglas.isPending ? (
        <SkeletonFilas filas={4} columnas={5} />
      ) : a.reglas.isError ? (
        <EstadoError
          err={a.reglas.error}
          que="las automatizaciones"
          onReintentar={() => void a.reglas.refetch()}
        />
      ) : !reglas.length ? (
        <EstadoVacio
          icono="bi-robot"
          titulo="Sin reglas"
          descripcion="Crea la primera automatización."
          accion={{ texto: 'Nueva regla', onClick: () => setEditando(null) }}
        />
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full text-3">
            <caption className="sr-only">Reglas de automatización</caption>
            <thead className="bg-sunken text-2">
              <tr>
                <th scope="col" className="p-2 text-left font-semibold">Regla</th>
                <th scope="col" className="p-2 text-left font-semibold">Evento</th>
                <th scope="col" className="p-2 text-left font-semibold">Modo</th>
                <th scope="col" className="p-2 text-left font-semibold">Canales</th>
                <th scope="col" className="p-2 text-left font-semibold">Estado</th>
                <th scope="col" className="p-2 text-right font-semibold">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {reglas.map((r) => (
                <tr key={r.id} className="border-t border-line">
                  <td className="p-2 font-semibold text-brand-ink">{r.name}</td>
                  <td className="p-2">{r.event_label}</td>
                  <td className="p-2">
                    {MODOS_DE_REGLA.find(([k]) => k === r.mode)?.[1] ?? r.mode}
                  </td>
                  <td className="p-2">
                    {(r.channels ?? [])
                      .map((c) => CANALES_DE_REGLA.find(([k]) => k === c)?.[1] ?? c)
                      .join(', ') || '—'}
                  </td>
                  <td className="p-2">
                    <Badge tone={r.is_active ? 'ok' : 'neutral'}>
                      {r.is_active ? 'Activa' : 'Inactiva'}
                    </Badge>
                  </td>
                  <td className="whitespace-nowrap p-1 text-right">
                    <Button variant="ghost" size="sm" onClick={() => setEditando(r.id)}>
                      <i className="bi bi-pencil" aria-hidden="true" />
                      <span className="sr-only">Editar {r.name}</span>
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setBorrando(r)}>
                      <i className="bi bi-trash text-danger-ink" aria-hidden="true" />
                      <span className="sr-only">Eliminar {r.name}</span>
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Bitacora consulta={a.bitacora} />

      {editando !== undefined && (
        <FormularioDeRegla
          id={editando}
          tipos={a.tipos.data ?? []}
          audiencias={a.audiencias.data ?? {}}
          plantillas={a.plantillas.data ?? []}
          plantillasFallaron={a.plantillas.isError}
          onCerrar={() => setEditando(undefined)}
        />
      )}

      {probando && (
        <ProbarEvento tipos={a.tipos.data ?? []} onCerrar={() => setProbando(false)} />
      )}

      <ConfirmDialog
        abierto={borrando != null}
        onCambio={(v) => { if (!v) setBorrando(null); }}
        titulo="Eliminar regla"
        descripcion={`«${borrando?.name ?? ''}» deja de dispararse. Las comunicaciones que ya generó no se tocan.`}
        textoConfirmar="Eliminar"
        destructivo
        cargando={borrar.isPending}
        onConfirmar={() => {
          if (!borrando) return;
          borrar.mutate(borrando.id, {
            onSuccess: () => { Utils.toast('Regla eliminada', 'success'); setBorrando(null); },
            onError: (e) => Utils.toast((e as Error).message, 'error'),
          });
        }}
      />
    </>
  );
}

const TONO_DE_ESTADO: Record<string, 'ok' | 'neutral' | 'warn' | 'danger'> = {
  procesado: 'ok', omitido: 'neutral', pendiente: 'warn', error: 'danger',
};

function Bitacora({ consulta }: { consulta: ReturnType<typeof useAutomatizaciones>['bitacora'] }) {
  const filas = consulta.data ?? [];
  return (
    <section className="mt-6">
      <h3 className="flex items-center gap-2 text-3 font-bold text-brand-ink">
        <i className="bi bi-clock-history" aria-hidden="true" /> Bitácora de eventos
      </h3>
      <div className="mt-3">
        {consulta.isPending ? (
          <SkeletonFilas filas={4} columnas={4} />
        ) : consulta.isError ? (
          <EstadoError
            err={consulta.error}
            que="la bitácora de eventos"
            onReintentar={() => void consulta.refetch()}
          />
        ) : !filas.length ? (
          <p className="text-3 text-muted">Sin eventos aún.</p>
        ) : (
          <Card className="max-h-[34vh] overflow-auto">
            <table className="w-full text-2">
              <caption className="sr-only">Eventos disparados y qué hizo cada uno</caption>
              <thead className="sticky top-0 bg-sunken">
                <tr>
                  <th scope="col" className="p-2 text-left font-semibold">Evento</th>
                  <th scope="col" className="p-2 text-left font-semibold">Estado</th>
                  <th scope="col" className="p-2 text-left font-semibold">Resultado</th>
                  <th scope="col" className="p-2 text-left font-semibold">Cuándo</th>
                </tr>
              </thead>
              <tbody>
                {filas.map((e, i) => {
                  const reglas = e.result?.rules_matched ?? 0;
                  const comunicaciones = (e.result?.communications ?? []).length;
                  return (
                    <tr key={`${e.created_at}-${i}`} className="border-t border-line">
                      <td className="p-2">{e.event_label}</td>
                      <td className="p-2">
                        <Badge tone={TONO_DE_ESTADO[e.status] ?? 'neutral'}>{e.status}</Badge>
                      </td>
                      <td className="p-2 text-muted">
                        {reglas} {reglas === 1 ? 'regla' : 'reglas'} ·{' '}
                        {comunicaciones} {comunicaciones === 1 ? 'comunicación' : 'comunicaciones'}
                      </td>
                      <td className="whitespace-nowrap p-2 text-muted">
                        {e.created_at ? Utils.fmtDatetime(e.created_at) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        )}
      </div>
    </section>
  );
}

/** El valor guardado del parámetro de audiencia, sea cual sea su campo. */
function parametroGuardado(r?: Regla | null): string {
  if (!r) return '';
  const config = PARAMETRO_DE_AUDIENCIA[r.audience_type ?? ''];
  if (!config) return '';
  const v = (r.audience_params ?? {})[config.campo];
  return v == null ? '' : String(v);
}

function FormularioDeRegla({
  id, tipos, audiencias, plantillas, plantillasFallaron, onCerrar,
}: {
  id: number | null;
  tipos: TipoDeEvento[];
  audiencias: OpcionesDeAudiencia;
  plantillas: Array<{ id: number; name: string }>;
  plantillasFallaron: boolean;
  onCerrar: () => void;
}) {
  const ficha = useRegla(id);
  const guardar = useGuardarRegla();
  const r = ficha.data;
  const cargando = id != null && ficha.isPending;

  const [listo, setListo] = useState(false);
  const [nombre, setNombre] = useState('');
  const [evento, setEvento] = useState('');
  const [modo, setModo] = useState<string>(MODOS_DE_REGLA[0][0]);
  const [canales, setCanales] = useState<string[]>(['feed']);
  const [plantilla, setPlantilla] = useState('');
  const [audiencia, setAudiencia] = useState('subject');
  const [parametro, setParametro] = useState('');
  const [titulo, setTitulo] = useState('');
  const [cuerpo, setCuerpo] = useState('');
  const [comoHtml, setComoHtml] = useState(false);
  const [respetaHorario, setRespetaHorario] = useState(true);
  const [activa, setActiva] = useState(true);

  if (!listo && !cargando && tipos.length) {
    setNombre(r?.name ?? '');
    setEvento(r?.event_type ?? tipos[0]!.key);
    setModo(r?.mode ?? MODOS_DE_REGLA[0][0]);
    setCanales(r?.channels ?? ['feed']);
    setPlantilla(r?.template_id ? String(r.template_id) : '');
    setAudiencia(r?.audience_type ?? 'subject');
    setParametro(parametroGuardado(r));
    setTitulo(r?.title_template ?? '');
    setCuerpo(r?.body_template ?? '');
    setComoHtml(Boolean(r?.is_html));
    setRespetaHorario(r ? Boolean(r.respect_working_hours) : true);
    setActiva(r ? Boolean(r.is_active) : true);
    setListo(true);
  }

  const variables = (tipos.find((t) => t.key === evento)?.context ?? [])
    .map((v) => `{{${v}}}`).join(' ');
  const usaPlantilla = Boolean(plantilla);
  const configParametro = PARAMETRO_DE_AUDIENCIA[audiencia];
  const opciones = configParametro ? audiencias[configParametro.lista] ?? [] : [];

  function enviar() {
    if (!nombre.trim()) { Utils.toast('El nombre es obligatorio', 'warning'); return; }
    if (!canales.length) { Utils.toast('Selecciona al menos un canal', 'warning'); return; }
    if (!usaPlantilla && (!titulo.trim() || !cuerpo.trim())) {
      Utils.toast('Elige una plantilla, o escribe título y contenido', 'warning');
      return;
    }
    const params: Record<string, unknown> = {};
    if (configParametro) {
      params[configParametro.campo] = parametro
        ? (configParametro.numerico ? parseInt(parametro, 10) : parametro)
        : null;
    }
    guardar.mutate({
      id,
      cuerpo: {
        name: nombre.trim(),
        event_type: evento,
        mode: modo,
        channels: canales,
        audience_type: audiencia,
        audience_params: params,
        title_template: titulo.trim(),
        body_template: cuerpo.trim(),
        template_id: parseInt(plantilla, 10) || null,
        is_html: comoHtml,
        respect_working_hours: respetaHorario,
        is_active: activa,
      },
    }, {
      onSuccess: () => { Utils.toast('Regla guardada', 'success'); onCerrar(); },
      onError: (e) => Utils.toast((e as Error).message, 'error'),
    });
  }

  return (
    <Dialog
      abierto
      onCambio={(v) => { if (!v) onCerrar(); }}
      ancho="max-w-2xl"
      titulo={id ? 'Editar regla' : 'Nueva regla'}
      pie={
        <>
          <Button variant="ghost" onClick={onCerrar}>Cancelar</Button>
          <Button onClick={enviar} loading={guardar.isPending}>Guardar regla</Button>
        </>
      }
    >
      {cargando ? (
        <SkeletonFilas filas={6} columnas={1} />
      ) : (
        <div className="flex flex-col gap-3">
          <Field label="Nombre">
            <Input value={nombre} onChange={(e) => setNombre(e.target.value)} />
          </Field>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Evento">
              <Select value={evento} onChange={(e) => setEvento(e.target.value)}>
                {tipos.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
              </Select>
            </Field>
            <Field label="Modo">
              <Select value={modo} onChange={(e) => setModo(e.target.value)}>
                {MODOS_DE_REGLA.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </Select>
            </Field>
          </div>

          <fieldset>
            <legend className="mb-1 text-2 font-semibold text-ink">Canales</legend>
            <div className="flex flex-wrap gap-4">
              {CANALES_DE_REGLA.map(([k, l]) => (
                <label key={k} className="flex items-center gap-2 text-3">
                  <input
                    type="checkbox"
                    checked={canales.includes(k)}
                    onChange={(e) => setCanales((previos) => (e.target.checked
                      ? [...previos, k]
                      : previos.filter((c) => c !== k)))}
                  />
                  {l}
                </label>
              ))}
            </div>
          </fieldset>

          <Field
            label="Plantilla"
            opcional
            ayuda={plantillasFallaron
              ? 'No se pudieron cargar las plantillas. Escribe el título y el contenido a mano, o reintenta más tarde.'
              : 'Si eliges una, sus variables se rellenan con los datos del evento.'}
          >
            <Select
              value={plantilla}
              onChange={(e) => setPlantilla(e.target.value)}
              disabled={plantillasFallaron}
            >
              <option value="">— Sin plantilla (usar título y contenido) —</option>
              {plantillas.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </Select>
          </Field>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Audiencia">
              <Select
                value={audiencia}
                onChange={(e) => {
                  // El parámetro se limpia SOLO si el campo cambia: volver al
                  // mismo tipo conserva lo elegido.
                  const nueva = e.target.value;
                  const antes = PARAMETRO_DE_AUDIENCIA[audiencia]?.campo;
                  const ahora = PARAMETRO_DE_AUDIENCIA[nueva]?.campo;
                  setAudiencia(nueva);
                  if (antes !== ahora) setParametro('');
                }}
              >
                {AUDIENCIAS_DE_REGLA.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </Select>
            </Field>
            {configParametro && (
              <Field label={AUDIENCIAS_DE_REGLA.find(([k]) => k === audiencia)?.[1] ?? 'Valor'}>
                <Select value={parametro} onChange={(e) => setParametro(e.target.value)}>
                  <option value="">— Seleccionar —</option>
                  {(opciones as Array<string | { id?: number; key?: string; name?: string }>)
                    .map((o) => {
                      const valor = typeof o === 'string' ? o : String(o.key ?? o.id ?? '');
                      const etiqueta = typeof o === 'string' ? o : (o.name ?? valor);
                      return <option key={valor} value={valor}>{etiqueta}</option>;
                    })}
                </Select>
              </Field>
            )}
          </div>

          {!usaPlantilla && (
            <>
              <Field label="Título">
                <Input
                  value={titulo}
                  onChange={(e) => setTitulo(e.target.value)}
                  placeholder="¡Bienvenido/a {{nombre_corto}}!"
                />
              </Field>
              <Field
                label="Contenido"
                ayuda={`Variables disponibles: ${variables || 'ninguna para este evento'}`}
              >
                <Textarea
                  rows={3}
                  value={cuerpo}
                  onChange={(e) => setCuerpo(e.target.value)}
                  placeholder="Mensaje con variables…"
                />
              </Field>
            </>
          )}

          <div className="flex flex-wrap gap-5">
            <label className="flex items-center gap-2 text-2">
              <input
                type="checkbox"
                checked={comoHtml}
                onChange={(e) => setComoHtml(e.target.checked)}
              />
              Contenido HTML
            </label>
            <label className="flex items-center gap-2 text-2">
              <input
                type="checkbox"
                checked={respetaHorario}
                onChange={(e) => setRespetaHorario(e.target.checked)}
              />
              Respetar el horario laboral
            </label>
            <label className="flex items-center gap-2 text-2">
              <input
                type="checkbox"
                checked={activa}
                onChange={(e) => setActiva(e.target.checked)}
              />
              Activa
            </label>
          </div>
        </div>
      )}
    </Dialog>
  );
}

function ProbarEvento({ tipos, onCerrar }: { tipos: TipoDeEvento[]; onCerrar: () => void }) {
  const disparar = useDispararEvento();
  const [evento, setEvento] = useState(tipos[0]?.key ?? '');
  const [uid, setUid] = useState('');

  return (
    <Dialog
      abierto
      onCambio={(v) => { if (!v) onCerrar(); }}
      titulo="Probar evento"
      descripcion="Emite el evento de verdad y aplica las reglas activas de ese tipo."
      pie={
        <>
          <Button variant="ghost" onClick={onCerrar}>Cancelar</Button>
          <Button
            loading={disparar.isPending}
            disabled={!evento}
            razonDeshabilitado="Elige un evento."
            onClick={() => disparar.mutate(
              { event_type: evento, ...(uid ? { subject_user_id: parseInt(uid, 10) } : {}) },
              {
                onSuccess: (res) => {
                  const n = res.result?.rules_matched ?? 0;
                  const c = (res.result?.communications ?? []).length;
                  Utils.toast(
                    `Evento ${res.status}: ${n} ${n === 1 ? 'regla' : 'reglas'}, `
                    + `${c} ${c === 1 ? 'comunicación' : 'comunicaciones'}`, 'success');
                  onCerrar();
                },
                onError: (e) => Utils.toast((e as Error).message, 'error'),
              })}
          >
            <i className="bi bi-lightning" aria-hidden="true" /> Disparar
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="flex items-start gap-2 rounded-sm border border-warn bg-warn/10 p-3 text-2 text-warn-ink">
          <i className="bi bi-exclamation-triangle mt-0.5 shrink-0" aria-hidden="true" />
          <span>
            Esto no es un ensayo: las reglas en modo automático <strong>envían de
            verdad</strong>. Si solo quieres comprobar la configuración, pon la regla en modo
            «Manual (borrador)» primero.
          </span>
        </p>
        <Field label="Evento">
          <Select value={evento} onChange={(e) => setEvento(e.target.value)}>
            {tipos.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </Select>
        </Field>
        <Field
          label="Número del colaborador"
          opcional
          ayuda="Para los eventos que hablan de una persona concreta. Es su identificador interno, no la cédula."
        >
          <Input
            type="number"
            inputMode="numeric"
            value={uid}
            onChange={(e) => setUid(e.target.value)}
            placeholder="Ej. 5"
          />
        </Field>
      </div>
    </Dialog>
  );
}
